import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  createEmployee,
  getEmployees,
  updateEmployee,
} from "../api";

const EMPTY_FORM = {
  full_name: "",
  email: "",
  password: "",
  role: "credit_specialist",
};

const ROLE_OPTIONS = [
  {
    value: "admin",
    label: "Administrator",
  },
  {
    value: "manager",
    label: "Manager",
  },
  {
    value: "credit_specialist",
    label: "Credit Specialist",
  },
  {
    value: "sales",
    label: "Sales",
  },
  {
    value: "staff",
    label: "Staff",
  },
];

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);

  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [editingEmployeeId, setEditingEmployeeId] = useState(null);
  const [editForm, setEditForm] = useState({
    full_name: "",
    role: "staff",
    is_active: true,
  });

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingEmployeeId, setSavingEmployeeId] = useState(null);

  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await getEmployees();
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setEmployees([]);
      setError(getErrorMessage(err, "Failed to load employees."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const summary = useMemo(() => {
    const result = {
      total: employees.length,
      active: 0,
      inactive: 0,
      admins: 0,
      managers: 0,
      specialists: 0,
    };

    employees.forEach((employee) => {
      if (employee.is_active) {
        result.active += 1;
      } else {
        result.inactive += 1;
      }

      const role = String(employee.role || "staff").toLowerCase();

      if (role === "admin") result.admins += 1;
      if (role === "manager") result.managers += 1;
      if (role === "credit_specialist") result.specialists += 1;
    });

    return result;
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return employees.filter((employee) => {
      const role = String(employee.role || "staff").toLowerCase();
      const isActive = Boolean(employee.is_active);

      const matchesSearch =
        !term ||
        String(employee.full_name || "")
          .toLowerCase()
          .includes(term) ||
        String(employee.email || "")
          .toLowerCase()
          .includes(term) ||
        getRoleLabel(role).toLowerCase().includes(term);

      const matchesRole =
        roleFilter === "all" || role === roleFilter;

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && isActive) ||
        (statusFilter === "inactive" && !isActive);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [employees, roleFilter, searchTerm, statusFilter]);

  function updateCreateField(field, value) {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }

  async function handleCreateEmployee(event) {
    event.preventDefault();

    if (!form.full_name.trim()) {
      setError("Please enter the employee's full name.");
      return;
    }

    if (!form.email.trim()) {
      setError("Please enter the employee's email.");
      return;
    }

    if (form.password.length < 8) {
      setError("The temporary password must contain at least 8 characters.");
      return;
    }

    try {
      setCreating(true);
      setError("");
      setSuccessMessage("");

      await createEmployee({
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
      });

      setForm(EMPTY_FORM);
      setSuccessMessage("Employee created successfully.");
      await loadEmployees();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to create employee."));
    } finally {
      setCreating(false);
    }
  }

  function beginEditing(employee) {
    setEditingEmployeeId(employee.id);

    setEditForm({
      full_name: employee.full_name || "",
      role: employee.role || "staff",
      is_active: Boolean(employee.is_active),
    });

    setError("");
    setSuccessMessage("");
  }

  function cancelEditing() {
    setEditingEmployeeId(null);

    setEditForm({
      full_name: "",
      role: "staff",
      is_active: true,
    });
  }

  async function handleSaveEmployee(employeeId) {
    if (!editForm.full_name.trim()) {
      setError("Employee full name cannot be blank.");
      return;
    }

    try {
      setSavingEmployeeId(employeeId);
      setError("");
      setSuccessMessage("");

      await updateEmployee(employeeId, {
        full_name: editForm.full_name.trim(),
        role: editForm.role,
        is_active: Boolean(editForm.is_active),
      });

      cancelEditing();
      setSuccessMessage("Employee updated successfully.");
      await loadEmployees();
    } catch (err) {
      setError(getErrorMessage(err, "Failed to update employee."));
    } finally {
      setSavingEmployeeId(null);
    }
  }

  async function handleToggleStatus(employee) {
    const nextStatus = !Boolean(employee.is_active);

    const actionLabel = nextStatus ? "activate" : "deactivate";

    const confirmed = window.confirm(
      `Are you sure you want to ${actionLabel} ${
        employee.full_name || employee.email
      }?`
    );

    if (!confirmed) return;

    try {
      setSavingEmployeeId(employee.id);
      setError("");
      setSuccessMessage("");

      await updateEmployee(employee.id, {
        full_name: employee.full_name || "",
        role: employee.role || "staff",
        is_active: nextStatus,
      });

      setSuccessMessage(
        nextStatus
          ? "Employee account activated."
          : "Employee account deactivated."
      );

      await loadEmployees();
    } catch (err) {
      setError(
        getErrorMessage(
          err,
          `Failed to ${actionLabel} employee.`
        )
      );
    } finally {
      setSavingEmployeeId(null);
    }
  }

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <div>
          <div style={eyebrowStyle}>Integrity Suite CRM</div>

          <h1 style={pageTitleStyle}>Employee Management</h1>

          <div style={pageSubtitleStyle}>
            Add team members, assign roles, review access, and manage account
            status.
          </div>
        </div>

        <button
          type="button"
          onClick={loadEmployees}
          style={refreshButtonStyle}
        >
          {loading ? "Refreshing..." : "Refresh Employees"}
        </button>
      </div>

      {error ? (
        <div style={errorMessageStyle}>{error}</div>
      ) : null}

      {successMessage ? (
        <div style={successMessageStyle}>{successMessage}</div>
      ) : null}

      <div style={summaryGridStyle}>
        <SummaryCard
          title="Total Employees"
          value={summary.total}
          subtitle="All team accounts"
        />

        <SummaryCard
          title="Active"
          value={summary.active}
          subtitle="Able to sign in"
        />

        <SummaryCard
          title="Inactive"
          value={summary.inactive}
          subtitle="Access disabled"
        />

        <SummaryCard
          title="Administrators"
          value={summary.admins}
          subtitle="Full system access"
        />

        <SummaryCard
          title="Credit Specialists"
          value={summary.specialists}
          subtitle="Case management team"
        />
      </div>

      <div style={mainGridStyle}>
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Add Employee</div>

          <div style={panelSubtitleStyle}>
            Create a team account and assign its initial permission level.
          </div>

          <form onSubmit={handleCreateEmployee}>
            <label style={labelStyle}>
              Full Name
              <input
                type="text"
                value={form.full_name}
                onChange={(event) =>
                  updateCreateField("full_name", event.target.value)
                }
                placeholder="Employee full name"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Email Address
              <input
                type="email"
                value={form.email}
                onChange={(event) =>
                  updateCreateField("email", event.target.value)
                }
                placeholder="employee@example.com"
                autoComplete="off"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Temporary Password
              <input
                type="password"
                value={form.password}
                onChange={(event) =>
                  updateCreateField("password", event.target.value)
                }
                placeholder="At least 8 characters"
                autoComplete="new-password"
                style={inputStyle}
              />
            </label>

            <label style={labelStyle}>
              Role
              <select
                value={form.role}
                onChange={(event) =>
                  updateCreateField("role", event.target.value)
                }
                style={inputStyle}
              >
                {ROLE_OPTIONS.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </select>
            </label>

            <RoleDescription role={form.role} />

            <button
              type="submit"
              disabled={creating}
              style={{
                ...primaryButtonStyle,
                opacity: creating ? 0.65 : 1,
              }}
            >
              {creating ? "Creating Employee..." : "Add Employee"}
            </button>
          </form>
        </div>

        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Role Permissions</div>

          <div style={panelSubtitleStyle}>
            These are the permission levels currently supported by Integrity
            Suite.
          </div>

          <div style={{ display: "grid", gap: "12px" }}>
            <PermissionCard
              title="Administrator"
              description="Full access to users, settings, clients, reports, AI tools, and all 
system controls."
            />

            <PermissionCard
              title="Manager"
              description="Manages employees and clients, reviews workflows, and accesses 
operational reports."
            />

            <PermissionCard
              title="Credit Specialist"
              description="Works client cases, credit reports, disputes, documents, letters, 
and AutoPilot."
            />

            <PermissionCard
              title="Sales"
              description="Designed for leads, onboarding, client intake, notes, and sales 
pipeline access."
            />

            <PermissionCard
              title="Staff"
              description="General internal staff access while detailed permission controls are 
completed."
            />
          </div>
        </div>
      </div>

      <div style={employeePanelStyle}>
        <div style={employeePanelHeaderStyle}>
          <div>
            <div style={panelHeaderStyle}>Team Directory</div>

            <div style={panelSubtitleStyle}>
              Showing {filteredEmployees.length} of {employees.length} team
              member(s).
            </div>
          </div>

          <div style={filterGroupStyle}>
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search employees"
              style={filterInputStyle}
            />

            <select
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              style={filterInputStyle}
            >
              <option value="all">All roles</option>

              {ROLE_OPTIONS.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              style={filterInputStyle}
            >
              <option value="all">All statuses</option>
              <option value="active">Active only</option>
              <option value="inactive">Inactive only</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={emptyStateStyle}>Loading employees...</div>
        ) : filteredEmployees.length === 0 ? (
          <div style={emptyStateStyle}>
            No employees matched the selected filters.
          </div>
        ) : (
          <div style={{ display: "grid", gap: "16px" }}>
            {filteredEmployees.map((employee) => {
              const isEditing = editingEmployeeId === employee.id;
              const isSaving = savingEmployeeId === employee.id;

              return (
                <div key={employee.id} style={employeeCardStyle}>
                  {isEditing ? (
                    <div style={editGridStyle}>
                      <label style={labelStyle}>
                        Full Name
                        <input
                          type="text"
                          value={editForm.full_name}
                          onChange={(event) =>
                            setEditForm((previous) => ({
                              ...previous,
                              full_name: event.target.value,
                            }))
                          }
                          style={inputStyle}
                        />
                      </label>

                      <label style={labelStyle}>
                        Role
                        <select
                          value={editForm.role}
                          onChange={(event) =>
                            setEditForm((previous) => ({
                              ...previous,
                              role: event.target.value,
                            }))
                          }
                          style={inputStyle}
                        >
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label style={checkboxLabelStyle}>
                        <input
                          type="checkbox"
                          checked={editForm.is_active}
                          onChange={(event) =>
                            setEditForm((previous) => ({
                              ...previous,
                              is_active: event.target.checked,
                            }))
                          }
                        />

                        Account Active
                      </label>

                      <div style={buttonRowStyle}>
                        <button
                          type="button"
                          onClick={() => handleSaveEmployee(employee.id)}
                          disabled={isSaving}
                          style={primarySmallButtonStyle}
                        >
                          {isSaving ? "Saving..." : "Save Changes"}
                        </button>

                        <button
                          type="button"
                          onClick={cancelEditing}
                          style={secondarySmallButtonStyle}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={employeeMainStyle}>
                        <div style={avatarStyle}>
                          {getInitials(employee.full_name, employee.email)}
                        </div>

                        <div style={{ flex: 1 }}>
                          <div style={employeeNameStyle}>
                            {employee.full_name || "Name Not Added"}
                          </div>

                          <div style={employeeEmailStyle}>
                            {employee.email}
                          </div>

                          <div style={employeeMetaRowStyle}>
                            <RoleBadge role={employee.role} />

                            <StatusBadge
                              active={Boolean(employee.is_active)}
                            />

                            <span style={employeeIdStyle}>
                              Employee ID: {employee.id}
                            </span>
                          </div>
                        </div>

                        <div style={lastLoginStyle}>
                          <div style={smallHeadingStyle}>Last Login</div>

                          <div>
                            {formatDate(employee.last_login)}
                          </div>

                          <div
                            style={{
                              ...smallHeadingStyle,
                              marginTop: "10px",
                            }}
                          >
                            Joined
                          </div>

                          <div>
                            {formatDate(employee.created_at)}
                          </div>
                        </div>
                      </div>

                      <div style={employeeActionsStyle}>
                        <button
                          type="button"
                          onClick={() => beginEditing(employee)}
                          style={primarySmallButtonStyle}
                        >
                          Edit Employee
                        </button>

                        <button
                          type="button"
                          onClick={() => handleToggleStatus(employee)}
                          disabled={isSaving}
                          style={{
                            ...secondarySmallButtonStyle,
                            color: employee.is_active
                              ? "#b91c1c"
                              : "#166534",
                          }}
                        >
                          {isSaving
                            ? "Updating..."
                            : employee.is_active
                            ? "Deactivate"
                            : "Activate"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function SummaryCard({ title, value, subtitle }) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryTitleStyle}>{title}</div>

      <div style={summaryValueStyle}>{value}</div>

      <div style={summarySubtitleStyle}>{subtitle}</div>
    </div>
  );
}

function PermissionCard({ title, description }) {
  return (
    <div style={permissionCardStyle}>
      <div style={permissionTitleStyle}>{title}</div>

      <div style={permissionDescriptionStyle}>{description}</div>
    </div>
  );
}

function RoleDescription({ role }) {
  const descriptions = {
    admin:
      "Administrators have complete control over users, settings, billing, and CRM data.",
    manager:
      "Managers can manage team members, clients, workflows, and operational reports.",
    credit_specialist:
      "Credit Specialists manage cases, reports, disputes, letters, documents, and AutoPilot.",
    sales:
      "Sales users are intended for client intake, leads, onboarding, and sales activity.",
    staff:
      "Staff users receive standard internal access while granular permissions are expanded.",
  };

  return (
    <div style={roleDescriptionStyle}>
      {descriptions[role] || descriptions.staff}
    </div>
  );
}

function RoleBadge({ role }) {
  const normalizedRole = String(role || "staff").toLowerCase();

  const styles = {
    admin: {
      background: "#ede9fe",
      color: "#6d28d9",
    },
    manager: {
      background: "#dbeafe",
      color: "#1d4ed8",
    },
    credit_specialist: {
      background: "#dcfce7",
      color: "#166534",
    },
    sales: {
      background: "#fef3c7",
      color: "#92400e",
    },
    staff: {
      background: "#e2e8f0",
      color: "#334155",
    },
  };

  const selectedStyle = styles[normalizedRole] || styles.staff;

  return (
    <span
      style={{
        ...pillStyle,
        ...selectedStyle,
      }}
    >
      {getRoleLabel(normalizedRole)}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      style={{
        ...pillStyle,
        background: active ? "#dcfce7" : "#fee2e2",
        color: active ? "#166534" : "#991b1b",
      }}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function getRoleLabel(role) {
  const match = ROLE_OPTIONS.find(
    (option) => option.value === String(role || "").toLowerCase()
  );

  return match ? match.label : "Staff";
}

function getInitials(fullName, email) {
  const name = String(fullName || "").trim();

  if (name) {
    const parts = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2);

    return parts
      .map((part) => part.charAt(0).toUpperCase())
      .join("");
  }

  return String(email || "E")
    .charAt(0)
    .toUpperCase();
}

function formatDate(value) {
  if (!value) return "Never";

  try {
    const normalizedValue = String(value).includes("T")
      ? value
      : String(value).replace(" ", "T");

    const date = new Date(normalizedValue);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  } catch {
    return value;
  }
}

function getErrorMessage(error, fallback) {
  const message = error?.message || "";

  try {
    const parsed = JSON.parse(message);

    if (parsed?.detail) {
      return parsed.detail;
    }
  } catch {
    // The API error was not JSON.
  }

  return message || fallback;
}

const pageStyle = {
  padding: "28px",
  minHeight: "100vh",
  background: "#f3f6fb",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "20px",
  flexWrap: "wrap",
  marginBottom: "26px",
};

const eyebrowStyle = {
  fontSize: "18px",
  color: "#64748b",
  fontWeight: "700",
  marginBottom: "8px",
};

const pageTitleStyle = {
  margin: 0,
  fontSize: "58px",
  lineHeight: 1,
  color: "#0f172a",
  fontWeight: "900",
};

const pageSubtitleStyle = {
  marginTop: "12px",
  color: "#64748b",
  fontSize: "17px",
};

const refreshButtonStyle = {
  padding: "16px 22px",
  borderRadius: "16px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "16px",
  fontWeight: "800",
  cursor: "pointer",
};

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "16px",
  marginBottom: "22px",
};

const summaryCardStyle = {
  padding: "20px",
  borderRadius: "20px",
  background: "white",
  border: "1px solid #e5e7eb",
  boxShadow: "0 8px 20px rgba(15,23,42,0.05)",
};

const summaryTitleStyle = {
  color: "#64748b",
  fontWeight: "800",
  marginBottom: "12px",
};

const summaryValueStyle = {
  color: "#0f172a",
  fontSize: "44px",
  fontWeight: "900",
  lineHeight: 1,
};

const summarySubtitleStyle = {
  color: "#94a3b8",
  marginTop: "10px",
};

const mainGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
  gap: "20px",
  marginBottom: "22px",
};

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const panelHeaderStyle = {
  fontSize: "28px",
  fontWeight: "900",
  color: "#0f172a",
  marginBottom: "8px",
};

const panelSubtitleStyle = {
  fontSize: "15px",
  color: "#64748b",
  marginBottom: "20px",
  lineHeight: 1.5,
};

const labelStyle = {
  display: "block",
  color: "#334155",
  fontWeight: "800",
  marginBottom: "14px",
};

const inputStyle = {
  width: "100%",
  marginTop: "8px",
  padding: "14px 16px",
  borderRadius: "14px",
  border: "1px solid #cbd5e1",
  background: "white",
  fontSize: "16px",
  boxSizing: "border-box",
};

const roleDescriptionStyle = {
  padding: "14px",
  marginBottom: "18px",
  borderRadius: "14px",
  background: "#eff6ff",
  border: "1px solid #bfdbfe",
  color: "#1e3a8a",
  lineHeight: 1.5,
};

const primaryButtonStyle = {
  width: "100%",
  padding: "16px",
  border: "none",
  borderRadius: "15px",
  background: "#D4AF37",
  color: "#111111",
  fontSize: "16px",
  fontWeight: "900",
  cursor: "pointer",
};

const permissionCardStyle = {
  padding: "15px",
  borderRadius: "15px",
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};

const permissionTitleStyle = {
  color: "#0f172a",
  fontWeight: "900",
  marginBottom: "6px",
};

const permissionDescriptionStyle = {
  color: "#64748b",
  lineHeight: 1.5,
  fontSize: "14px",
};

const employeePanelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const employeePanelHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "18px",
  flexWrap: "wrap",
  marginBottom: "20px",
};

const filterGroupStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const filterInputStyle = {
  padding: "12px 14px",
  borderRadius: "12px",
  border: "1px solid #cbd5e1",
  background: "white",
  fontSize: "14px",
  minWidth: "150px",
};

const employeeCardStyle = {
  padding: "18px",
  borderRadius: "18px",
  border: "1px solid #e5e7eb",
  background: "#f8fafc",
};

const employeeMainStyle = {
  display: "flex",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
};

const avatarStyle = {
  width: "58px",
  height: "58px",
  borderRadius: "18px",
  background: "#dbeafe",
  color: "#1d4ed8",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "20px",
  fontWeight: "900",
  flexShrink: 0,
};

const employeeNameStyle = {
  color: "#0f172a",
  fontSize: "21px",
  fontWeight: "900",
};

const employeeEmailStyle = {
  color: "#64748b",
  marginTop: "4px",
};

const employeeMetaRowStyle = {
  display: "flex",
  gap: "8px",
  flexWrap: "wrap",
  alignItems: "center",
  marginTop: "10px",
};

const pillStyle = {
  display: "inline-block",
  padding: "7px 10px",
  borderRadius: "999px",
  fontSize: "13px",
  fontWeight: "900",
};

const employeeIdStyle = {
  color: "#94a3b8",
  fontSize: "13px",
};

const lastLoginStyle = {
  minWidth: "180px",
  color: "#475569",
  fontSize: "14px",
};

const smallHeadingStyle = {
  color: "#94a3b8",
  fontSize: "12px",
  fontWeight: "900",
  textTransform: "uppercase",
  letterSpacing: "0.8px",
  marginBottom: "4px",
};

const employeeActionsStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  marginTop: "16px",
  paddingTop: "16px",
  borderTop: "1px solid #e5e7eb",
};

const primarySmallButtonStyle = {
  padding: "11px 15px",
  borderRadius: "12px",
  border: "none",
  background: "#D4AF37",
  color: "#111111",
  fontWeight: "800",
  cursor: "pointer",
};

const secondarySmallButtonStyle = {
  padding: "11px 15px",
  borderRadius: "12px",
  border: "1px solid #cbd5e1",
  background: "white",
  color: "#0f172a",
  fontWeight: "800",
  cursor: "pointer",
};

const editGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px",
  alignItems: "end",
};

const checkboxLabelStyle = {
  display: "flex",
  gap: "10px",
  alignItems: "center",
  color: "#334155",
  fontWeight: "800",
  paddingBottom: "18px",
};

const buttonRowStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
  paddingBottom: "12px",
};

const errorMessageStyle = {
  padding: "16px 18px",
  marginBottom: "20px",
  borderRadius: "16px",
  border: "2px solid #ef4444",
  background: "#ffffff",
  color: "#991b1b",
  fontWeight: "700",
};

const successMessageStyle = {
  padding: "16px 18px",
  marginBottom: "20px",
  borderRadius: "16px",
  border: "2px solid #22c55e",
  background: "#ffffff",
  color: "#166534",
  fontWeight: "700",
};

const emptyStateStyle = {
  padding: "22px",
  borderRadius: "16px",
  border: "1px dashed #cbd5e1",
  background: "#f8fafc",
  color: "#94a3b8",
};
